/*
 * A copy of the content, kept in the browser between visits.
 *
 * Without it every arrival at the site would cost nine of the thousand calls
 * the workspace is allowed each month, which a few dozen readers would finish
 * off. With it a reader costs those nine calls once, and nothing at all for the
 * next two hours however much they move around the site or reload it.
 */

const STORAGE_KEY = 'gauri-awasthi-content';

/*
 * Raised when the shape of the stored content changes, so that a browser
 * holding yesterday's shape reads it as absent rather than handing the pages
 * fields they no longer understand.
 */
const CACHE_VERSION = 1;

const MINUTE = 60 * 1000;

/*
 * Two hours, less a margin.
 *
 * The margin is there because of the pictures. Airtable hands out signed links
 * to attachments that stop working two hours after they are issued, and those
 * links are in the copy being kept here. Were the copy trusted for the full two
 * hours, a reader arriving at the end of that window would be given links about
 * to expire and would watch the images fail. Refreshing a little early means
 * every link handed to a reader still has time left on it.
 */
export const CACHE_TTL_MS = (120 * MINUTE) - (10 * MINUTE);

/* Told to go too fast, the sensible thing is to wait a moment and try again. */
const RATE_LIMIT_PAUSE_MS = 5 * MINUTE;

/*
 * The month's allowance, though, does not come back until the base's month
 * turns over. Trying again on every page view would spend the first calls of
 * the new month on failures, so the site stops asking for a good while.
 */
const MONTHLY_LIMIT_PAUSE_MS = 6 * 60 * MINUTE;

const storage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    /* Blocked by the browser's settings; the site works, it just re-fetches. */
    return null;
  }
};

const readEntry = () => {
  const store = storage();
  if (!store) return null;

  try {
    const entry = JSON.parse(store.getItem(STORAGE_KEY));
    return entry?.version === CACHE_VERSION ? entry : null;
  } catch (error) {
    return null;
  }
};

const writeEntry = (entry) => {
  const store = storage();
  if (!store) return;

  try {
    store.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (error) {
    /* Out of room, or private browsing. Not worth failing a page render over. */
  }
};

/*
 * A reader who wants to see an edit straight away can add ?refresh to the
 * address. The query sits before the # so it survives the router.
 */
export const refreshRequested = () => (
  typeof window !== 'undefined' && /[?&]refresh\b/.test(window.location.href)
);

const EXPIRING_IMAGE = /airtableusercontent\.com/i;
const IMAGE_COLLECTIONS = ['films', 'editorial', 'twentyEightDaysStills', 'gallery'];

/*
 * The words in an old copy are still worth showing while fresher ones are
 * fetched; the image links in it are, by then, dead. Dropping them lets each
 * page fall back to the picture bundled with the app, which is a better sight
 * than a broken one.
 */
export const withoutExpiredImages = (content) => {
  const cleaned = { ...content };

  IMAGE_COLLECTIONS.forEach((key) => {
    if (!Array.isArray(cleaned[key])) return;
    cleaned[key] = cleaned[key].map((row) => (
      EXPIRING_IMAGE.test(row.imageUrl || '') ? { ...row, imageUrl: '' } : row
    ));
  });

  if (cleaned.siteImages) {
    cleaned.siteImages = Object.fromEntries(
      Object.entries(cleaned.siteImages).filter(([, url]) => !EXPIRING_IMAGE.test(url || '')),
    );
  }

  return cleaned;
};

/*
 * What is stored, and what the caller should do about it: use it as it stands,
 * show it while fetching over the top of it, or ignore it.
 */
export const readCache = () => {
  const entry = readEntry();
  if (!entry) return null;

  const now = Date.now();
  const paused = Boolean(entry.pausedUntil && entry.pausedUntil > now);
  const fresh = Boolean(entry.content) && (now - entry.fetchedAt) < CACHE_TTL_MS;

  /*
   * Nothing held and nothing to wait out, so there is nothing here to spare
   * the caller a fetch. A pause with no content is still worth reporting: it
   * happens when the month's calls ran out before anything could be read, and
   * repeating those refused calls on every page view would be the worst of
   * both, spending the new month's first calls on failures.
   */
  if (!entry.content && !paused) return null;

  let content = entry.content || null;
  if (content && !fresh) {
    content = withoutExpiredImages(content);
  }

  return {
    content,
    fetchedAt: entry.fetchedAt || null,
    /* Fresh, or old with Airtable currently not worth asking again. */
    usable: fresh || paused,
    fresh,
    paused,
  };
};

export const saveCache = (content) => {
  writeEntry({ version: CACHE_VERSION, fetchedAt: Date.now(), content });
};

/*
 * Kept alongside whatever content is already stored, so a refusal costs the
 * site its freshness but not its copy of the content.
 */
export const pauseFetching = ({ monthlyLimitReached }) => {
  const entry = readEntry() || { version: CACHE_VERSION, fetchedAt: 0, content: null };
  const pause = monthlyLimitReached ? MONTHLY_LIMIT_PAUSE_MS : RATE_LIMIT_PAUSE_MS;
  writeEntry({ ...entry, pausedUntil: Date.now() + pause });
};

export const clearCache = () => {
  const store = storage();
  if (store) store.removeItem(STORAGE_KEY);
};
